import { useRecoilValue } from 'recoil';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { userState } from 'state';
import { useWizardConfig } from '../context';
import { useWizardNav } from '../navigation-context';
import { useBuildCutsData } from '../services/use-build-cuts-data';
import type {
  CutsheetWizardFormValues,
  ExtraOption,
  SpecificationGroup,
  TrimOption,
} from '../types';

export function ReviewContent({
  cutGroups,
  selectedIds,
  specGroups,
  isPreviewMode = false,
  trimOptions,
  extrasOptions,
  values,
}: {
  cutGroups: ReturnType<typeof useBuildCutsData>['cutsByPrimalName'];
  selectedIds: string[];
  specGroups: SpecificationGroup[];
  isPreviewMode?: boolean;
  trimOptions: TrimOption[];
  extrasOptions: ExtraOption[];
  values: CutsheetWizardFormValues;
}) {
  const user = useRecoilValue(userState);
  const { settings, animalSpecies, inspectionLevel } = useWizardConfig();
  const nav = useWizardNav();

  const fmtUSD = (n: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(n ?? 0);

  const fmtPerLb = (n?: number) =>
    typeof n === 'number' ? `${fmtUSD(n)}/lb` : '—';

  /**
   * Derive base processing price per pound from processor settings.
   *
   * The pricing structure in ProcessorSettings is nested:
   * settings.animalSettings[] -> array of animal configurations (beef, pork, lamb, etc.)
   *   └─> .customCutsheetPricing[] -> array of pricing entries per inspection level
   *       └─> .price -> the actual price per pound
   *
   * Algorithm:
   * 1. Find the animalSettings entry matching the current species (beef, pork, etc.)
   * 2. Within that entry, find the customCutsheetPricing matching the inspection level (exempt, state, federal)
   * 3. Extract the price value
   *
   * This price is used in the Financial Summary to show customers the estimated
   * base processing cost before extras/trim are added.
   */
  const deriveBaseProcessingPricePerLb = (): number | undefined => {
    const s = settings;
    // Navigate the nested structure: settings -> animalSettings -> customCutsheetPricing
    if (s?.animalSettings && Array.isArray(s.animalSettings)) {
      // Step 1: Find matching species configuration
      const animalSetting = s.animalSettings.find(
        (setting) => setting?.species === animalSpecies,
      );
      if (
        animalSetting?.customCutsheetPricing &&
        Array.isArray(animalSetting.customCutsheetPricing)
      ) {
        // Step 2: Find matching inspection level pricing
        const pricingEntry = animalSetting.customCutsheetPricing.find(
          (entry) => entry?.inspectionLevel === inspectionLevel,
        );
        // Step 3: Return price if found
        if (typeof pricingEntry?.price === 'number') return pricingEntry.price;
      }
    }
    return undefined;
  };
  const basePerLb = deriveBaseProcessingPricePerLb();

  const cutsSummary = (
    <>
      {cutGroups.map((g) => {
        const selected = g.cuts.filter((c) => selectedIds.includes(c._id));
        if (!selected.length) return null;
        return (
          <Card
            key={g.primalId}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {g.primalName}
                  </Typography>
                  <Stack spacing={0.5}>
                    {selected.map((c) => (
                      <Typography key={c._id} variant="body2">
                        {c.name}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => nav.goTo && nav.goTo(g.primalId)}
                >
                  Edit
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </>
  );

  const specsSummary = (
    <>
      {specGroups.map((group) => {
        const selected = group.options.filter(
          (option) =>
            values.selectedSpecifications?.[group.key] === option.value,
        );
        if (!selected.length) return null;
        return (
          <Card
            key={group.key}
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 2,
                }}
              >
                <Box>
                  <Stack spacing={0.5}>
                    {selected.map((option) => {
                      const displayValue = group.measurementUnit
                        ? `${option.label} ${group.measurementUnit}`
                        : option.label;
                      return (
                        <Box key={option.value}>
                          <Typography
                            variant="h6"
                            sx={{ color: 'text.secondary' }}
                          >
                            {group.title}
                          </Typography>
                          <Typography variant="body2">
                            {displayValue}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => nav.goTo && nav.goTo(`spec:${group.key}`)}
                >
                  Edit
                </Button>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </>
  );

  const trimsSummary = (
    <Card
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Trim
            </Typography>
            <Stack spacing={0.5}>
              {values.trim
                .slice()
                .sort((a, b) => (a?.rank || 99) - (b?.rank || 99))
                .map((selection) => {
                  const t = trimOptions.find((o) => o.value === selection.name);
                  if (!t) return null;
                  const isPercentageMode =
                    values.trimSelection === 'allowSelectionByPercentage';
                  const quantityDisplay = isPercentageMode
                    ? `${selection.quantity}%`
                    : `${selection.quantity} lbs`;
                  return (
                    <Typography key={selection.name} variant="body2">
                      {`Priority ${selection.rank}: ${t.label} - ${quantityDisplay} @ ${fmtUSD(t.pricePerPound)}/lb`}
                    </Typography>
                  );
                })}
            </Stack>
          </Box>
          <Button
            variant="outlined"
            onClick={() => nav.goTo && nav.goTo('trim')}
          >
            Edit
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const extrasSummary = (
    <Card
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Extras
            </Typography>
            <Stack spacing={0.5}>
              {values.extras
                .filter((e) => e.isActive)
                .map((extra) => {
                  const e = extrasOptions.find((o) => o.label === extra.name);
                  if (!e) return null;
                  if (e.type === 'flatFee') {
                    return (
                      <Typography key={extra.name} variant="body2">{`${
                        e.label
                      } - ${fmtUSD(e.fee ?? 0)}`}</Typography>
                    );
                  }
                  if (e.type === 'pricedPerPound' || e.type === 'pricePerLb') {
                    const quantityText = extra.quantity
                      ? ` • ${extra.quantity} lbs`
                      : '';
                    return (
                      <Typography key={extra.name} variant="body2">{`${
                        e.label
                      } / ${fmtUSD(e.pricePerPound ?? 0)} per lb${quantityText}`}</Typography>
                    );
                  }
                  return (
                    <Typography key={extra.name} variant="body2">
                      {e.label}
                    </Typography>
                  );
                })}
            </Stack>
          </Box>
          <Button
            variant="outlined"
            onClick={() => nav.goTo && nav.goTo('extras')}
          >
            Edit
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <>
      {specsSummary}
      {cutsSummary}
      {extrasOptions.length > 0 && extrasSummary}
      {trimOptions.length > 0 && trimsSummary}

      {/* Financial Summary */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'tertiary.main',
          backgroundColor: (theme) =>
            alpha(
              theme.palette?.tertiary?.main ?? theme.palette.primary.main,
              0.1,
            ),
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography
            variant="h6"
            sx={{ color: 'primary.main', fontWeight: 700, mb: 1 }}
          >
            Financial Summary
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
              Base Processing Price:
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {fmtPerLb(basePerLb)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Typography
            variant="subtitle1"
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            Selected Options:
          </Typography>
          <Stack spacing={0.5}>
            {/* Extras */}
            {values.extras
              .filter((extra) => extra.isActive)
              .map((extra) => {
                const e = extrasOptions.find((o) => o.label === extra.name);
                if (!e) return null;
                if (e.type === 'flatFee') {
                  return (
                    <Box
                      key={`extra-${extra.name}`}
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2">{e.label}:</Typography>
                      <Typography variant="body2">
                        {fmtUSD(e.fee ?? 0)}
                      </Typography>
                    </Box>
                  );
                }
                if (e.type === 'pricedPerPound' || e.type === 'pricePerLb') {
                  const estimatedCost =
                    extra.quantity && e.pricePerPound
                      ? extra.quantity * e.pricePerPound
                      : undefined;
                  return (
                    <Box
                      key={`extra-${extra.name}`}
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2">
                        {e.label}
                        {extra.quantity ? ` (${extra.quantity} lbs)` : ''}:
                      </Typography>
                      <Typography variant="body2">
                        {estimatedCost
                          ? fmtUSD(estimatedCost)
                          : fmtPerLb(e.pricePerPound)}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Box
                    key={`extra-${extra.name}`}
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2">{e.label}</Typography>
                    <Typography variant="body2">—</Typography>
                  </Box>
                );
              })}

            {/* Trim */}
            {values.trim.map((selection) => {
              const t = trimOptions.find((o) => o.value === selection.name);
              if (!t) return null;

              // Always show price per pound for trim (cost will be calculated when animal weight is known)
              return (
                <Box
                  key={`trim-${selection.name}`}
                  sx={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2">{t.label}:</Typography>
                  <Typography variant="body2">
                    {fmtPerLb(t.pricePerPound)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
          {isPreviewMode && (
            <Alert severity="info" icon={false} sx={{ mt: 2 }}>
              This is a preview of the cutsheet wizard your customers will see.
              Click &quot;Complete&quot; to finish the preview and reset for
              another walkthrough.
            </Alert>
          )}
        </CardContent>
      </Card>
    </>
  );
}
